/**
 * On-device Web Speech dictation adapter. Same role as assistant-ui's built-in
 * `WebSpeechDictationAdapter`, but forces local processing (`processLocally`) so
 * audio never leaves the machine, and pre-downloads the on-device language pack
 * via the `available()` / `install()` API (Chrome 138+).
 *
 * Behaviour mirrors the upstream adapter (status machine, interim + final
 * results); the only differences are `processLocally = true` and the install
 * step. Where on-device speech isn't available, `isSupported()` returns false, no
 * dictation adapter is registered, and the composer mic button is hidden entirely.
 */
import type { DictationAdapter } from "@assistant-ui/react";

/**
 * Minimal shape of the on-device Web Speech API — the `processLocally` flag and
 * the static `available()`/`install()` methods aren't in the DOM lib typings yet.
 */
type LocalSpeechRecognition = {
	lang: string;
	continuous: boolean;
	interimResults: boolean;
	processLocally: boolean;
	start: () => void;
	stop: () => void;
	abort: () => void;
	addEventListener: (type: string, listener: (event: Event) => void) => void;
};

type AvailabilityStatus = "available" | "downloadable" | "downloading" | "unavailable";

/** `SpeechRecognitionEvent` isn't in the DOM lib typings; this is the slice we read. */
type SpeechResultEvent = {
	resultIndex: number;
	results: ArrayLike<SpeechRecognitionResult>;
};

type LocalSpeechRecognitionConstructor = {
	new (): LocalSpeechRecognition;
	/** Resolves whether on-device recognition is ready for the given languages. */
	available?: (options: { langs: string[]; processLocally?: boolean }) => Promise<AvailabilityStatus>;
	/** Triggers download of the on-device language pack. */
	install?: (options: { langs: string[]; processLocally?: boolean }) => Promise<boolean>;
};

function getSpeechRecognitionApi(): LocalSpeechRecognitionConstructor | undefined {
	if (typeof window === "undefined") return undefined;
	return (window.SpeechRecognition ?? window.webkitSpeechRecognition) as unknown as
		| LocalSpeechRecognitionConstructor
		| undefined;
}

export class LocalWebSpeechDictationAdapter implements DictationAdapter {
	private readonly _language: string;

	constructor(options: { language?: string } = {}) {
		const defaultLanguage = typeof navigator !== "undefined" && navigator.language ? navigator.language : "en-US";
		this._language = options.language ?? defaultLanguage;
	}

	static isSupported(): boolean {
		return getSpeechRecognitionApi() !== undefined;
	}

	listen(): DictationAdapter.Session {
		const SpeechRecognitionApi = getSpeechRecognitionApi();
		if (!SpeechRecognitionApi) {
			throw new Error("SpeechRecognition is not supported in this browser.");
		}

		const recognition = new SpeechRecognitionApi();
		recognition.lang = this._language;
		recognition.continuous = true;
		recognition.interimResults = true;
		// The load-bearing line: require on-device processing, no cloud round-trip.
		recognition.processLocally = true;

		const speechStartCallbacks = new Set<() => void>();
		const speechEndCallbacks = new Set<(result: DictationAdapter.Result) => void>();
		const speechCallbacks = new Set<(result: DictationAdapter.Result) => void>();
		let finalTranscript = "";

		const session: DictationAdapter.Session = {
			status: { type: "starting" },
			stop: async () => {
				recognition.stop();
				return new Promise<void>((resolve) => {
					const checkEnded = () => {
						if (session.status.type === "ended") resolve();
						else setTimeout(checkEnded, 50);
					};
					checkEnded();
				});
			},
			cancel: () => {
				recognition.abort();
			},
			onSpeechStart: (callback) => {
				speechStartCallbacks.add(callback);
				return () => speechStartCallbacks.delete(callback);
			},
			onSpeechEnd: (callback) => {
				speechEndCallbacks.add(callback);
				return () => speechEndCallbacks.delete(callback);
			},
			onSpeech: (callback) => {
				speechCallbacks.add(callback);
				return () => speechCallbacks.delete(callback);
			},
		};

		recognition.addEventListener("speechstart", () => {
			for (const cb of speechStartCallbacks) cb();
		});
		recognition.addEventListener("start", () => {
			session.status = { type: "running" };
		});
		recognition.addEventListener("result", (event) => {
			const speechEvent = event as unknown as SpeechResultEvent;
			for (let i = speechEvent.resultIndex; i < speechEvent.results.length; i++) {
				const result = speechEvent.results[i];
				if (!result) continue;
				const transcript = result[0]?.transcript ?? "";
				if (result.isFinal) finalTranscript += transcript;
				for (const cb of speechCallbacks) cb({ transcript, isFinal: result.isFinal });
			}
		});
		recognition.addEventListener("end", () => {
			if (session.status.type !== "ended") session.status = { type: "ended", reason: "stopped" };
			if (finalTranscript) {
				for (const cb of speechEndCallbacks) cb({ transcript: finalTranscript });
				finalTranscript = "";
			}
		});
		recognition.addEventListener("error", (event) => {
			const errorEvent = event as unknown as { error: string; message?: string };
			if (errorEvent.error === "aborted") {
				session.status = { type: "ended", reason: "cancelled" };
			} else {
				session.status = { type: "ended", reason: "error" };
				console.error("[local-webspeech] dictation error:", errorEvent.error, errorEvent.message);
			}
		});

		// Ensure the on-device language pack is present, then start. This runs inside
		// the mic-click's user-activation window, which `install()` (a download) needs —
		// doing it at construction (no gesture) is why the first attempt failed with
		// `language-not-supported`. listen() must stay sync, so we start asynchronously
		// and let the status machine drive the UI in the meantime.
		const begin = async () => {
			try {
				if (SpeechRecognitionApi.available) {
					const langs = [recognition.lang];
					const availability = await SpeechRecognitionApi.available({ langs, processLocally: true });
					if (availability === "unavailable") {
						session.status = { type: "ended", reason: "error" };
						console.warn(
							`[local-webspeech] on-device recognition unavailable for "${recognition.lang}" on this platform.`,
						);
						return;
					}
					// "downloadable" | "downloading" → fetch the pack before starting.
					if (availability !== "available") await SpeechRecognitionApi.install?.({ langs, processLocally: true });
				}
				recognition.start();
			} catch (error) {
				session.status = { type: "ended", reason: "error" };
				console.error("[local-webspeech] could not start on-device recognition", error);
			}
		};
		void begin();

		return session;
	}
}
